import React, { useState, useEffect, useCallback } from "react";
import { Pagination, Stack, FormControlLabel, Switch } from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppSelectFilter from "../components/common/AppSelectFilter";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import AnnouncementCard from "../components/announcements/AnnouncementCard";
import CreateAnnouncementModal from "../components/announcements/CreateAnnouncementModal";
import UpdateAnnouncementModal from "../components/announcements/UpdateAnnouncementModal";
import ViewAnnouncementModal from "../components/announcements/ViewAnnouncementModal";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getAnnouncements,
  getAnnouncementById,
  deleteAnnouncement,
  resetAnnouncementServerStatus,
} from "../redux/actions/announcementActions";
import { AnnouncementSummaryDto } from "../shared/dtos/announcement/AnnouncementSummaryDto";
import { AnnouncementResponseDto } from "../shared/dtos/announcement/AnnouncementResponseDto";
import { AnnouncementAudience } from "../shared/enums/AnnouncementAudience";
import { ANNOUNCEMENT_AUDIENCE_OPTIONS } from "../shared/data/enumOptions";

const AnnouncementsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: announcements,
    item: announcement,
    pagination,
    loadingFetchAll: loadingAnnouncements,
    loadingFetchOne: loadingAnnouncement,
    errorFetchAll: errorLoadingAnnouncements,
    errorFetchOne: errorLoadingAnnouncement,
    serverStatus,
    loadingCreate: loadingAnnouncementCreate,
    errorCreate: errorCreatingAnnouncement,
    loadingUpdate: loadingAnnouncementUpdate,
    errorUpdate: errorUpdatingAnnouncement,
    loadingDelete: loadingAnnouncementDelete,
  } = useAppSelector((state) => state.announcement);

  // Filters State
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce(searchTerm, 500);

  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedAudience, setSelectedAudience] = useState<string>("");
  const [includeExpired, setIncludeExpired] = useState<boolean>(false);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 12 });

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] =
    useState<AnnouncementSummaryDto | null>(null);

  // Permissions
  const canCreate = hasPermission("announcement:create");
  const canUpdate = hasPermission("announcement:update");
  const canDelete = hasPermission("announcement:delete");
  const canRead = hasPermission("announcement:read");

  const fetchAnnouncementsList = useCallback((): void => {
    dispatch(
      getAnnouncements({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch.trim() || undefined,
        branchId: selectedBranchId || undefined,
        audience: (selectedAudience as AnnouncementAudience) || undefined,
        includeExpired,
      }),
    );
  }, [
    dispatch,
    pageConfig.page,
    pageConfig.limit,
    debouncedSearch,
    selectedBranchId,
    selectedAudience,
    includeExpired,
  ]);

  useEffect(() => {
    fetchAnnouncementsList();
  }, [fetchAnnouncementsList]);

  // Modal Handlers
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingAnnouncementCreate) setIsCreateModalOpen(false);
  }, [loadingAnnouncementCreate]);

  const handleOpenUpdate = (item: AnnouncementSummaryDto): void => {
    dispatch(getAnnouncementById(item.id));
    setSelectedItem(item);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingAnnouncementUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedItem(null);
    }
  }, [loadingAnnouncementUpdate]);

  const handleOpenDelete = (item: AnnouncementSummaryDto): void => {
    setSelectedItem(item);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingAnnouncementDelete) {
      setIsDeleteModalOpen(false);
      setSelectedItem(null);
    }
  }, [loadingAnnouncementDelete]);

  const handleOpenView = (item: AnnouncementSummaryDto): void => {
    dispatch(getAnnouncementById(item.id));
    setIsViewModalOpen(true);
  };
  const handleCloseView = (): void => setIsViewModalOpen(false);

  const handleDeleteConfirm = (): void => {
    if (selectedItem) {
      dispatch(deleteAnnouncement(selectedItem.id));
    }
  };

  // Auto-close on success
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetAnnouncementServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="Announcements Hub"
      subtitle="Publish and manage school-wide and targeted communications."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<CampaignIcon />}
            onClick={handleOpenCreate}
          >
            New Announcement
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPageConfig((prev) => ({ ...prev, page: 1 }));
        }}
        searchPlaceholder="Search announcements..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={(bId) => {
            setSelectedBranchId(bId);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
        />

        <AppSelectFilter
          label="Audience"
          value={selectedAudience}
          onChange={(val) => {
            setSelectedAudience(val);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
          options={ANNOUNCEMENT_AUDIENCE_OPTIONS}
          allLabel="All Audiences"
          minWidth={160}
        />

        <FormControlLabel
          control={
            <Switch
              checked={includeExpired}
              onChange={(e) => {
                setIncludeExpired(e.target.checked);
                setPageConfig((prev) => ({ ...prev, page: 1 }));
              }}
              color="secondary"
            />
          }
          label="Include Expired"
          sx={{ ml: 1, userSelect: "none" }}
        />
      </PageToolbar>

      <CardGridContainer
        loading={loadingAnnouncements}
        error={errorLoadingAnnouncements}
        itemCount={announcements?.length || 0}
        emptyMessage="No Announcements Found"
        onRetry={fetchAnnouncementsList}
      >
        {announcements.map((item: AnnouncementSummaryDto) => (
          <AnnouncementCard
            key={item.id}
            announcement={item}
            onView={canRead ? () => handleOpenView(item) : undefined}
            onEdit={canUpdate ? () => handleOpenUpdate(item) : undefined}
            onDelete={canDelete ? () => handleOpenDelete(item) : undefined}
          />
        ))}
      </CardGridContainer>

      {pagination?.totalPages && pagination.totalPages > 1 && (
        <Stack sx={{ alignItems: "center", mt: 4 }}>
          <Pagination
            count={pagination.totalPages}
            page={pageConfig.page}
            onChange={(_, value) =>
              setPageConfig((prev) => ({ ...prev, page: value }))
            }
            color="secondary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}

      {/* CREATE MODAL */}
      {isCreateModalOpen && (
        <CreateAnnouncementModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingAnnouncementCreate}
          error={errorCreatingAnnouncement}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateModalOpen && selectedItem && (
        <UpdateAnnouncementModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          announcement={
            (announcement?.id === selectedItem.id
              ? announcement
              : selectedItem) as AnnouncementResponseDto
          }
          loading={loadingAnnouncementUpdate || loadingAnnouncement}
          error={errorUpdatingAnnouncement || errorLoadingAnnouncement}
        />
      )}

      {/* VIEW MODAL */}
      {isViewModalOpen && (
        <ViewAnnouncementModal
          open={isViewModalOpen}
          onClose={handleCloseView}
          announcement={announcement}
          loading={loadingAnnouncement}
          error={errorLoadingAnnouncement}
        />
      )}

      {/* DELETE CONFIRM DIALOG */}
      {isDeleteModalOpen && selectedItem && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Announcement"
          description={`Are you sure you want to delete the announcement "${selectedItem.title}"?`}
          loading={loadingAnnouncementDelete}
        />
      )}
    </PageContainer>
  );
};

export default AnnouncementsPage;
